import styles from './MapWithCompetitors.module.css'
import { APIProvider, Marker } from '@vis.gl/react-google-maps'
import { Map } from '@vis.gl/react-google-maps'
import { HostServiceMarker } from './presentationals/HostServiceMarker'
import { CSSProperties, useCallback, useEffect, useState } from 'react'
import { HostServiceDetailWindow } from './presentationals/HostServiceDetailWindow'
import { MapCameraChangedEvent, MapCameraProps } from '@vis.gl/react-google-maps';
import classNames from 'classnames'
import { CircularProgress } from '@mui/material'
import { useBreakpointUp } from 'shared/hooks/useBreakpointQueries'
import { usePrevious } from 'shared/hooks/usePrevious'
import { quickSelectMedian } from 'shared/lib/quickSelectMedian'
import { dbscanGeo } from 'shared/lib/dbscanGeo'
import {useMemo} from 'react'

const INITIAL_CAMERA = {
    center: { lat: 40.78, lng: -73.95},
    zoom: 14
};

const API_KEY = `AIzaSyDX87U6r9-PPzM5R57-2eAkgJhy6lLUGgE`

interface MapWithCompetitorsProps{
    count? : number
    hostServices?: any[]
    selectedHostService?: any
    selectedHostServices?: any[]
    hostServicesFilteredByMapBounds?: any[]
    location?: any
    onMarkerClick?: (hostService: any) => void
    onCloseDetailWindow: (event: any) => void
    onStartPropertySpy: (event: any) => void
    onStopPropertySpy: (event: any) => void
    onMapBoundsChanged?: (event: any) => void
    spyId?: number
    loading?: boolean
}


export const MapWithCompetitors = (props: MapWithCompetitorsProps): JSX.Element => {
    const { 
        hostServices,
        selectedHostService,
        selectedHostServices,
        hostServicesFilteredByMapBounds,
        location,
        spyId,
        onMarkerClick,
        onStartPropertySpy,
        onStopPropertySpy,
        onCloseDetailWindow,
        onMapBoundsChanged,
        loading
    } = props

    const previousSpyId = usePrevious(spyId)
    const previousHostServices: any = usePrevious(hostServices)
    const isXlWidthScreenOrMore = useBreakpointUp('xl')
    const [markers, setMarkers] = useState<any>([])
    const [markerForCenteringByLocation, setMarkerForCenteringByLocation] = useState<any>([])

    const handleMarkerClick = (event: any) => {
        onMarkerClick?.(event)
    }

    useEffect(() => {
        let markers: any = []

        if (hostServicesFilteredByMapBounds?.length !== undefined &&  hostServicesFilteredByMapBounds?.length > 0){
            const sliceHostServices = hostServicesFilteredByMapBounds

            for (let index = sliceHostServices.length - 1; index >= 0; index--) {
            
                const hostService = sliceHostServices[index]
                
                markers.push((
                <HostServiceMarker
                    key={hostService.id}
                    hostService={hostService}
                    onClick={handleMarkerClick}
                    selected={false}
                    index={index}
                />
            ))
            }
        }

        setMarkers(markers)
    }, [
        hostServicesFilteredByMapBounds,
        onMarkerClick
    ])

    let windows: any = []

    if (isXlWidthScreenOrMore){
        if (selectedHostService !== undefined){
            const index = hostServices?.findIndex((allhs) => allhs.id === selectedHostService.id) || 0
            windows.push(
                    (
                    <HostServiceDetailWindow
                        key={selectedHostService.id}
                        hostService={selectedHostService}
                        spyId={spyId}
                        onStartPropertySpy={onStartPropertySpy}
                        onStopPropertySpy={onStopPropertySpy}
                        onClose={onCloseDetailWindow}
                        index={index}
                    />
                )
            )
        }

    }

    const mapStyle: CSSProperties = {
        borderRadius: '16px',
        background: '#FFF',
        overflow: 'auto',
    }

    const [cameraProps, setCameraProps] = useState<MapCameraProps>(INITIAL_CAMERA)
    const [cameraPropsHasChanges, setCameraPropsHasChanges] = useState<boolean>(false)


    useEffect(() => {
        if (location === undefined){
            setCameraProps(INITIAL_CAMERA)
            return
        }


        if (hostServices === undefined){
            setCameraProps((prev: any) => {
                return {
                    ...prev,
                    center: {
                        lat: location.boxSWLatitude,
                        lng: location.boxSWLongitude
                    }
                }
            })
            return
        }


        const inRange: any = []

        hostServices.forEach((hostService) => {
            const inRangeLatitude = hostService.latitude >=location.boxSWLatitude && hostService.latitude <= location.boxNELatitude
            const inRangeLongitude = hostService.longitude >=location.boxSWLongitude && hostService.longitude <= location.boxNELongitude

            if (inRangeLatitude && inRangeLongitude){
                inRange.push(hostService)
            }
        })

        const dbscanResult = dbscanGeo(inRange, 2, 2)

        const medianlatitude = quickSelectMedian(dbscanResult.largestCluster.map((hostService: any) => hostService.latitude))
        const medianLongitude = quickSelectMedian(dbscanResult.largestCluster.map((hostService: any) => hostService.longitude))
        if (medianlatitude === null || medianLongitude === null){
                        setCameraProps((prev: any) => {
                return {
                    ...prev,
                    center: {
                        lat: location.boxSWLatitude,
                        lng: location.boxSWLongitude
                    },
                    zoom: INITIAL_CAMERA.zoom
                }
            })
            return
        }


        setCameraProps((prev: any) => {
                return {
                    ...prev,
                    center: {
                        lat: medianlatitude,
                        lng: medianLongitude
                    },
                    zoom: INITIAL_CAMERA.zoom
                }
            })


    }, [
        location,
        hostServices,
        isXlWidthScreenOrMore
    ])


    const handleCameraChange = useCallback((ev: MapCameraChangedEvent) => {
        setCameraProps(ev.detail)
    }, [])

    const mapWithCompetitorsLoaderClasses = classNames(styles.MapWithCompetitors__Loader, {
        [styles.MapWithCompetitors__Loader__Show]: loading,
    })

    const mapWithCompetitorsLoaderBgClasses = classNames(styles.MapWithCompetitors__Loader__Bg, {
        [styles.MapWithCompetitorsLoader__Bg__Show]: loading,
    })

    const handleMapBoundsChanged = (event: any) => {
        onMapBoundsChanged?.(event)
    }

    return (
        <div className={styles.MapWithCompetitors__Root}>
            <div className={mapWithCompetitorsLoaderClasses}>
                <div className={mapWithCompetitorsLoaderBgClasses}></div>
                <div className={styles.MapWithCompetitors__Loader__Spinner}>
                    <div>Loading</div>
                    <div><CircularProgress size={80} /></div>
                </div>
            </div>
            <APIProvider apiKey={API_KEY}>
                <Map    
                    style={mapStyle}
                    mapId={'a36892df908962b433b2ea6e'}
                    {...cameraProps}
                    gestureHandling={'greedy'}
                    disableDefaultUI
                    onCameraChanged={handleCameraChange}
                    onBoundsChanged={handleMapBoundsChanged}
                >
                    {markerForCenteringByLocation}
                    {markers}
                    {windows}
                </Map>
            </APIProvider>
        </div>
    )
}